import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Icon,
  IconButton,
  Button,
} from '@material-ui/core';

import { convertObjToArr } from '../../../../../../utils';
import Actions from '../../../../../../store/ducks/appointments';
import SliderWindow from '../../../../../../components/SliderWindow';

import { useSchedule } from '../../context';

import Filters from './Filters';
import List from './List';
import ListItem from './List/ListItem';
import Details from './Details';
import useStyles from './styles';

const ProfessionalModal = ({ onExited }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { list: professionals, allLoaded } = useSelector(
    state => state.appointments.professionals,
  );
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [professionalsList, setProfessionalsList] = useState([]);
  const [professionalsListFiltered, setProfessionalsListFiltered] = useState(
    [],
  );
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [paginate, setPaginate] = useState(0);
  const { setFilterProfessional } = useSchedule();

  useEffect(() => {
    if (open && !allLoaded) {
      dispatch(Actions.requestProfessionals());
    } else {
      setLoading(false);
    }
  }, [dispatch, allLoaded, open]);

  useEffect(() => {
    setProfessionalsList(convertObjToArr(professionals));
  }, [professionals]);

  useEffect(() => {
    setProfessionalsListFiltered(professionalsList);
  }, [professionalsList]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  const normalizeStr = useCallback(
    str => str.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
    [],
  );

  const resetFilters = useCallback(() => {
    setProfessionalsListFiltered(professionalsList);
  }, [professionalsList]);

  const handleSearch = useCallback(
    value => {
      if (value) {
        const regex = new RegExp(normalizeStr(value), 'i');
        const listFiltered = professionalsList.filter(
          ({ name, areaName }) =>
            regex.test(normalizeStr(name)) ||
            regex.test(normalizeStr(areaName)),
        );
        setProfessionalsListFiltered(listFiltered);
      } else {
        resetFilters();
      }
    },
    [normalizeStr, professionalsList, resetFilters],
  );

  const handleSelectArea = useCallback(
    value => {
      if (value) {
        const listFiltered = professionalsList.filter(
          ({ areaId }) => areaId === value,
        );
        setProfessionalsListFiltered(listFiltered);
      } else {
        resetFilters();
      }
    },
    [professionalsList, resetFilters],
  );

  const handleSelectProfessional = useCallback(id => {
    setSelectedProfessional(id);
    setPaginate(1);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedProfessional(null);
    setPaginate(-1);
  }, []);

  const handleFilterProfessional = useCallback(() => {
    setFilterProfessional(selectedProfessional);
    setOpen(false);
  }, [selectedProfessional, setFilterProfessional]);

  return (
    <Dialog
      className={classes.container}
      classes={{ paper: classes.paper }}
      onExited={onExited}
      onClose={handleCloseModal}
      open={open}
      maxWidth="xs"
    >
      <DialogTitle disableTypography className="modal-title-container">
        <Typography className="modal-title-text">
          Filtrar profissional
        </Typography>
        <IconButton
          className="modal-title-btn-close"
          onClick={handleCloseModal}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <Box className="modal-subtitle-container">
        <Filters
          onBack={handleBackToList}
          onSearch={handleSearch}
          onSelectArea={handleSelectArea}
          professionals={professionalsList}
          selectedProfessional={selectedProfessional}
        />
      </Box>
      <DialogContent className="modal-content-container" dividers>
        {loading ? (
          <ListItem loading />
        ) : (
          <SliderWindow goTo={paginate} removeTransition={!open}>
            <List
              list={professionalsListFiltered}
              onSelect={handleSelectProfessional}
            />
            <Details id={selectedProfessional} />
          </SliderWindow>
        )}
      </DialogContent>
      <DialogActions>
        <Button className="btn-close" onClick={handleCloseModal}>
          Cancelar
        </Button>
        {selectedProfessional && (
          <Button
            className="btn-filter"
            onClick={handleFilterProfessional}
            color="primary"
          >
            Filtrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProfessionalModal;
